import { isEmpty } from './is-empty';
import { isNotDefined } from './is-not-defined.guard';

/**
 * Converts a given value to a boolean (`true` or `false`).
 *
 * - If the value is `undefined`, `null`, or considered "empty" (as determined by `isEmpty`),
 *   returns the provided `defaultValue` (defaults to `false`).
 * - If the value is a string, returns `true` only if the string (case-insensitive, trimmed)
 *   is `'1'` or `'true'`; otherwise, returns `false`.
 * - For all other types, returns the boolean coercion (`!!value`).
 *
 * @template T - The type of the value to be coerced.
 * @param value - The value to be converted to boolean.
 * @param defaultValue - The value to return if `value` is empty or undefined. Defaults to `false`.
 * @returns `true` or `false` based on the coercion logic.
 */
export const coerceBoolean = <T = unknown>(value: T, defaultValue = false): boolean => {
  if (isNotDefined(value)) {
    return defaultValue;
  }

  if (isEmpty(value)) {
    return defaultValue;
  }

  if (typeof value === 'string') {
    const valueString = value.trim().toLowerCase();

    switch (valueString) {
      case '1':

      case 'true':
        return true;

      default:
        return false;
    }
  }

  return !!value;
};
